import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Upload, FileSpreadsheet, Loader2, Sun, TrendingDown, Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const num = (v) => (v === "" || v === null || v === undefined ? "" : Number(v));

const SUPPLIERS = ["Sparki XL", "Engie", "Luminus", "Eneco"];
const SUPPLIER_ROWS = [
  { label: "Tarif total (c€/kWh)", cells: ["27,03", "42,75", "46,34", "42,81"] },
  { label: "% plus cher que Sparki XL", cells: ["Référence", "+55,56 %", "+70,37 %", "+55,57 %"] },
  { label: "Panneaux solaires offerts", cells: ["yes", "no", "no", "no"] },
  { label: "Batterie offerte", cells: ["yes", "no", "no", "no"] },
  { label: "Prix fixe garanti", cells: ["25 ans", "1-2 ans max", "1-2 ans max", "1-2 ans max"] },
  { label: "Coût annuel (3.500 kWh)", cells: ["946 €", "~1.496 €", "~1.622 €", "~1.498 €"] },
];

const SupplierCell = ({ v, highlight }) => {
  let content = v;
  if (v === "yes") content = <span className="inline-flex items-center gap-1 font-semibold text-emerald-600"><Check className="h-4 w-4" /> Oui</span>;
  else if (v === "no") content = <span className="inline-flex items-center gap-1 text-muted-foreground"><X className="h-4 w-4" /> Non</span>;
  return (
    <td className={`px-4 py-3.5 text-center ${highlight ? "bg-accent/10 font-bold text-primary" : "text-foreground"}`}>
      {content}
    </td>
  );
};

const SupplierComparison = () => (
  <section data-testid="supplier-comparison" className="mb-10 overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
    <div className="border-b border-border bg-primary px-6 py-5 text-primary-foreground">
      <h2 className="font-display text-xl font-bold sm:text-2xl">La comparaison : ce que vous économisez vraiment</h2>
      <p className="mt-1 text-sm text-primary-foreground/70">Source : Vtest.be — Tarif fixe juillet 2026</p>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[680px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            <th className="px-4 py-4 text-left font-semibold text-muted-foreground">Fournisseur</th>
            {SUPPLIERS.map((s, i) => (
              <th
                key={s}
                className={`px-4 py-4 text-center font-display font-bold ${i === 0 ? "bg-accent/10 text-primary" : "text-foreground"}`}
              >
                {s} {i === 0 && <span className="text-accent">★</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SUPPLIER_ROWS.map((row, ri) => (
            <tr key={ri} className="border-b border-border/60 last:border-0">
              <td className="px-4 py-3.5 text-left font-medium text-muted-foreground">{row.label}</td>
              {row.cells.map((c, ci) => (
                <SupplierCell key={ci} v={c} highlight={ci === 0} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="border-t border-border px-6 py-4 text-xs leading-relaxed text-muted-foreground">
      * Le tarif Sparki XL intègre les frais de transport et taxes. La différence avec les fournisseurs classiques est de −0,26 €/kWh. TVA 6 % incluse dans les prix Sparki XL.
    </div>
    <div className="flex flex-col items-center gap-3 border-t border-border bg-muted/30 px-6 py-6 sm:flex-row sm:justify-between">
      <p className="text-sm font-medium text-foreground">
        Envie de bloquer ce tarif de <span className="font-bold text-primary">0,27 €/kWh</span> pendant 25 ans ?
      </p>
      <Button asChild className="h-11 rounded-full bg-accent px-6 text-base hover:bg-accent/90" data-testid="comparison-cta-button">
        <a href="/#rendez-vous">Prendre rendez-vous</a>
      </Button>
    </div>
  </section>
);

export default function Comparateur() {
  const [form, setForm] = useState({
    fournisseur: "", acompte_verse: "", decompte: "",
    conso_jour_kwh: "", conso_nuit_kwh: "", kwh_injectes: "",
    has_panels: true, injection_known: true, rendement: 30, prix_xl: 0.2703,
  });
  const [extracting, setExtracting] = useState(false);
  const [computing, setComputing] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/extract-bill", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        fournisseur: data.fournisseur ?? f.fournisseur,
        acompte_verse: data.acompte_verse ?? f.acompte_verse,
        decompte: data.decompte ?? f.decompte,
        conso_jour_kwh: data.conso_jour_kwh ?? f.conso_jour_kwh,
        conso_nuit_kwh: data.conso_nuit_kwh ?? f.conso_nuit_kwh,
        kwh_injectes: data.kwh_injectes ?? f.kwh_injectes,
        injection_known: (data.kwh_injectes ?? 0) > 0 ? true : f.injection_known,
      }));
      toast.success("Document analysé ! Vérifiez les champs pré-remplis.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lecture du document impossible.");
    } finally {
      setExtracting(false);
    }
  };

  const payload = () => ({
    fournisseur: form.fournisseur,
    acompte_verse: num(form.acompte_verse) || 0,
    decompte: num(form.decompte) || 0,
    conso_jour_kwh: num(form.conso_jour_kwh) || 0,
    conso_nuit_kwh: num(form.conso_nuit_kwh) || 0,
    kwh_injectes: num(form.kwh_injectes) || 0,
    has_panels: form.has_panels,
    injection_known: form.injection_known,
    rendement: num(form.rendement) || 0,
    prix_xl: num(form.prix_xl) || 0.2703,
  });

  const compute = async () => {
    setComputing(true);
    try {
      const { data } = await api.post("/compare", payload());
      setResult(data);
    } catch (e) {
      toast.error("Erreur de calcul.");
    } finally {
      setComputing(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const res = await api.post("/compare/excel", payload(), { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "comparaison_energie.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export Excel impossible.");
    }
  };

  const fields = [
    { k: "fournisseur", l: "Fournisseur actuel", type: "text" },
    { k: "acompte_verse", l: "Acomptes versés (€)", type: "number" },
    { k: "decompte", l: "Décompte / solde (€)", type: "number" },
    { k: "conso_jour_kwh", l: "Conso. jour (kWh)", type: "number" },
    { k: "conso_nuit_kwh", l: "Conso. nuit (kWh)", type: "number" },
    { k: "kwh_injectes", l: "kWh injectés", type: "number" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-primary px-6 py-6 text-primary-foreground">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent"><Sun className="h-5 w-5" /></span>
            <div>
              <h1 className="font-display text-xl font-bold">Comparateur d'énergie</h1>
              <p className="text-sm text-primary-foreground/70">Photo/PDF de facture → économies calculées</p>
            </div>
          </div>
          <Button asChild variant="secondary" size="sm" className="rounded-full">
            <Link to="/" data-testid="comparateur-back-link"><ArrowLeft className="mr-1.5 h-4 w-4" /> Accueil</Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        <SupplierComparison />
        <label
          data-testid="upload-dropzone"
          className="mb-8 flex cursor-pointer flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-border bg-card p-10 text-center transition-colors hover:border-accent"
        >
          <input data-testid="bill-file-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} disabled={extracting} />
          {extracting ? <Loader2 className="h-10 w-10 animate-spin text-accent" /> : <Upload className="h-10 w-10 text-accent" />}
          <p className="font-display text-lg font-bold">{extracting ? "Analyse du document..." : "Importer une facture (photo ou PDF)"}</p>
          <p className="text-sm text-muted-foreground">L'IA lit la facture de clôture annuelle et pré-remplit les champs.</p>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="comparateur-privacy-notice">
            Votre facture est utilisée uniquement pour calculer votre comparatif. Voir notre{" "}
            <Link to="/confidentialite" className="text-accent underline">politique de confidentialité</Link>.
          </p>
        </label>

        <div className="grid gap-8 lg:grid-cols-2">
          <div className="rounded-3xl border border-border bg-card p-6">
            <h2 className="mb-4 font-display text-xl font-bold">Données de la facture</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.k}>
                  <Label className="mb-1.5 block text-sm">{f.l}</Label>
                  <Input data-testid={`field-${f.k}`} type={f.type} value={form[f.k]} onChange={(e) => set(f.k, e.target.value)} className="h-11" />
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-4">
              <div className="flex items-center justify-between rounded-xl bg-muted px-4 py-3">
                <Label className="text-sm">Le client possède des panneaux solaires</Label>
                <Switch data-testid="switch-panels" checked={form.has_panels} onCheckedChange={(v) => set("has_panels", v)} />
              </div>
              {form.has_panels && (
                <div className="flex items-center justify-between rounded-xl bg-muted px-4 py-3">
                  <Label className="text-sm">Injection (kWh) connue</Label>
                  <Switch data-testid="switch-injection" checked={form.injection_known} onCheckedChange={(v) => set("injection_known", v)} />
                </div>
              )}
              {form.has_panels && !form.injection_known && (
                <div>
                  <Label className="mb-1.5 block text-sm">Rendement moyen des panneaux (%)</Label>
                  <Input data-testid="field-rendement" type="number" value={form.rendement} onChange={(e) => set("rendement", e.target.value)} className="h-11" />
                </div>
              )}
            </div>
            <Button data-testid="compute-button" onClick={compute} disabled={computing} className="mt-6 h-12 w-full rounded-full bg-accent text-base hover:bg-accent/90">
              {computing ? "Calcul..." : "Calculer mes économies"}
            </Button>
          </div>

          <div className="space-y-6">
            {result ? (
              <>
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl bg-primary p-6 text-primary-foreground" data-testid="result-summary">
                  <div className="flex items-center gap-2 text-sm opacity-80"><TrendingDown className="h-4 w-4" /> Économie estimée</div>
                  <p className="mt-2 font-display text-5xl font-extrabold tracking-tighter text-secondary">
                    {result.economie_annuelle.toLocaleString("fr-BE")} €<span className="text-lg font-medium opacity-70">/an</span>
                  </p>
                  <p className="mt-1 text-sm opacity-80">Soit ~{result.economie_25ans.toLocaleString("fr-BE")} € cumulés sur 25 ans.</p>
                </motion.div>

                <div className="rounded-3xl border border-border bg-card p-6" data-testid="result-table">
                  <table className="w-full text-sm">
                    <tbody>
                      {[
                        ["Consommation totale", `${result.conso_totale_kwh} kWh`],
                        ["Coût annuel actuel", `${result.cout_actuel.toLocaleString("fr-BE")} €`],
                        ["Prix actuel", `${result.prix_kwh_actuel} €/kWh`],
                        ["Notre offre", `${result.prix_kwh_xl} €/kWh`],
                        ["Facture avec notre offre", `${result.facture_xl.toLocaleString("fr-BE")} €`],
                      ].map(([k, v], i) => (
                        <tr key={i} className="border-b border-border/60 last:border-0">
                          <td className="py-2.5 text-muted-foreground">{k}</td>
                          <td className="py-2.5 text-right font-semibold">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="rounded-3xl border border-border bg-card p-6">
                  <h3 className="mb-4 flex items-center gap-2 font-display font-bold"><Sparkles className="h-4 w-4 text-accent" /> Projection sur 25 ans</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={result.projection}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={44} />
                      <Tooltip formatter={(v) => `${v} €`} />
                      <Legend />
                      <Line type="monotone" dataKey="market_bill" name="Marché" stroke="#E06D53" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="xl_bill" name="Notre offre" stroke="#0F3D32" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="rounded-3xl border border-border bg-card p-6" data-testid="result-amortization">
                  <h3 className="mb-1 font-display font-bold">Tableau d'amortissement</h3>
                  <p className="mb-4 text-sm text-muted-foreground">
                    Base {result.install_cost.toLocaleString("fr-BE")} € · amortissement linéaire sur 25 ans.
                  </p>
                  <div className="max-h-64 overflow-auto rounded-xl border border-border">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-muted">
                        <tr className="text-left">
                          <th className="p-2 font-semibold">Année</th>
                          <th className="p-2 font-semibold">Amort./an</th>
                          <th className="p-2 font-semibold">Amort. total</th>
                          <th className="p-2 text-right font-semibold">Valeur résiduelle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.amortization.map((a) => (
                          <tr key={a.year} className="border-t border-border/60">
                            <td className="p-2">{a.year}</td>
                            <td className="p-2">{a.amort_year.toLocaleString("fr-BE")} €</td>
                            <td className="p-2">{a.amort_total.toLocaleString("fr-BE")} €</td>
                            <td className="p-2 text-right font-medium">{a.residual.toLocaleString("fr-BE")} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="rounded-3xl border border-border bg-card p-6" data-testid="result-purchase">
                  <h3 className="mb-1 font-display font-bold">Achat de panneaux vs offre XL</h3>
                  <p className="mb-3 text-sm text-muted-foreground">
                    Comparaison entre acheter soi-même son installation et la formule XL (sans panneaux au départ).
                  </p>
                  <div className="mb-4 rounded-xl bg-primary p-4 text-primary-foreground">
                    <p className="text-sm opacity-80">Avantage total estimé de l'offre XL sur 25 ans</p>
                    <p className="font-display text-3xl font-extrabold tracking-tighter text-secondary" data-testid="advantage-total">
                      {result.advantage_total.toLocaleString("fr-BE")} €
                    </p>
                    <p className="mt-1 text-xs opacity-70">
                      dont {result.advantage_25ans.toLocaleString("fr-BE")} € + {result.battery_replacement.toLocaleString("fr-BE")} € (batterie de remplacement)
                    </p>
                  </div>
                  <div className="max-h-64 overflow-auto rounded-xl border border-border">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-muted">
                        <tr className="text-left">
                          <th className="p-2 font-semibold">Année</th>
                          <th className="p-2 font-semibold">Achat</th>
                          <th className="p-2 font-semibold">Offre XL</th>
                          <th className="p-2 text-right font-semibold">Cumul</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.purchase_vs_xl.map((p) => (
                          <tr key={p.year} className="border-t border-border/60">
                            <td className="p-2">{p.year}</td>
                            <td className="p-2">{p.achat.toLocaleString("fr-BE")} €</td>
                            <td className="p-2">{p.xl.toLocaleString("fr-BE")} €</td>
                            <td className="p-2 text-right font-medium">{p.cumulative.toLocaleString("fr-BE")} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <Button data-testid="excel-download-button" onClick={downloadExcel} variant="outline" className="h-12 w-full rounded-full">
                  <FileSpreadsheet className="mr-2 h-5 w-5" /> Télécharger le fichier Excel
                </Button>
              </>
            ) : (
              <div className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-3xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground">
                <FileSpreadsheet className="h-12 w-12 opacity-30" />
                <p className="mt-4">Importez une facture ou remplissez les champs, puis calculez pour voir votre comparaison ici.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
